// ============================================
// CHÁMOMILE - Product Service
// Fetches products dynamically from Firestore
// ============================================

import { collection, doc, getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Product } from '../types';
import { products as fallbackProducts } from '../data/products';

/**
 * Fetch products from Firestore 'products' collection.
 * - Filters to onlineAvailable !== false
 * - Uses Firestore doc ID as product ID
 * - Falls back to hardcoded products on error
 */
export const fetchOnlineProducts = async (): Promise<Product[]> => {
    try {
        const productsRef = collection(db, 'products');
        const q = query(productsRef, orderBy('name'));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            console.warn('[ProductService] No products in Firestore, using fallback');
            return fallbackProducts;
        }

        const products: Product[] = [];

        snapshot.forEach((doc) => {
            const data = doc.data();

            // Skip products not available online
            if (data.onlineAvailable === false) return;

            // Rewards-only items (e.g. the VIP key-chain) are never purchasable.
            if (data.redemptionOnly === true) return;

            products.push({
                id: doc.id,
                name: data.name || 'Unnamed Product',
                imageUrl: data.imageUrl || '',
                price: typeof data.price === 'number' ? data.price : parseFloat(data.price) || 0,
                description: data.description || '',
                category: data.category || '',
                featured: data.featured || false,
                // Beverage flavour groupings — drive the menu tabs.
                tags: Array.isArray(data.tags) ? data.tags : [],
                // Set by the POS size selector; groups sizes into one card.
                sizeGroup: data.sizeGroup || '',
                size: data.size || '',
                redemptionOnly: data.redemptionOnly === true,
                // Live sellable count maintained by the POS. `null` = unknown
                // (field missing), which the app treats as sellable — only a
                // real 0 should ever block a sale. Enforced for merch/food
                // only; drinks are brewed to order and ignore this.
                stock: Number.isFinite(parseInt(data.stock)) ? parseInt(data.stock) : null,
            } as Product);
        });

        if (products.length === 0) {
            console.warn('[ProductService] No online-available products, using fallback');
            return fallbackProducts;
        }

        console.log(`[ProductService] Loaded ${products.length} products from Firestore`);
        return products;
    } catch (error) {
        console.error('[ProductService] Error fetching products:', error);
        return fallbackProducts;
    }
};

/**
 * Fetch categories and their icons from Firestore 'settings/categories' document
 */
export const fetchCategoriesAndIcons = async (): Promise<{categories: string[], categoryIcons: Record<string, string>, categoryImages: Record<string, string>, categoryKinds: Record<string, string>}> => {
    const defaults = {
        categories: ['Beverage', 'Merch'],
        categoryIcons: {
            'Signature': '🍵', 'Classic': '☕', 'Viral': '🔥', 'Seasonal': '🌸'
        } as Record<string, string>,
        categoryImages: {} as Record<string, string>,
        // A category's TYPE drives cart rules, not its name. POS-managed in
        // Settings → Categories, so a category can be renamed or added without
        // a code change here.
        categoryKinds: { Beverage: 'drink', Merch: 'merch', Food: 'food' } as Record<string, string>,
    };

    try {
        const snapshot = await getDoc(doc(db, 'settings', 'categories'));
        if (!snapshot.exists()) return defaults;

        const data = snapshot.data();
        return {
            categories: Array.isArray(data.list) && data.list.length > 0 ? data.list : defaults.categories,
            categoryIcons: data.icons || defaults.categoryIcons,
            categoryImages: data.images || defaults.categoryImages,
            // Merge so categories without an explicit type still resolve.
            categoryKinds: { ...defaults.categoryKinds, ...(data.kinds || {}) },
        };
    } catch (error) {
        console.error('[ProductService] Error fetching categories:', error);
        return defaults;
    }
};

/**
 * Fetch beverage tags and their icons from Firestore 'settings/productTags'.
 *
 * Tags are the customer-facing menu tabs for drinks. They are POS-managed
 * (Settings → Beverage Tags), so new tags appear here without a code change.
 */
export const fetchProductTags = async (): Promise<{tags: string[], tagIcons: Record<string, string>, tagImages: Record<string, string>}> => {
    const defaults = {
        tags: ['Classic', 'Signature', 'Viral', 'Seasonal'],
        tagIcons: { 'Signature': '🍵', 'Classic': '☕', 'Viral': '🔥', 'Seasonal': '🌸' } as Record<string, string>,
        tagImages: {} as Record<string, string>,
    };

    try {
        const snapshot = await getDoc(doc(db, 'settings', 'productTags'));
        if (!snapshot.exists()) return defaults;

        const data = snapshot.data();
        return {
            tags: Array.isArray(data.list) && data.list.length > 0 ? data.list : defaults.tags,
            tagIcons: data.icons || defaults.tagIcons,
            tagImages: data.images || defaults.tagImages,
        };
    } catch (error) {
        console.error('[ProductService] Error fetching product tags:', error);
        return defaults;
    }
};

export default { fetchOnlineProducts, fetchCategoriesAndIcons, fetchProductTags };
